import React, {useEffect, useState} from 'react';
import {ActionModal, ErrorModal, FormInput, useErrorHandler} from '../../../../shared';
import {useAddAppealStoryForm} from '../../model';
import {ImageUploadButton, PreparedImageData} from '../../../fundraiserImages';
import {Button, Icon, Text} from '@gravity-ui/uikit';
import {DatePicker} from '@gravity-ui/date-components';
import {DateTime} from '@gravity-ui/date-utils';
import styles from './AddAppealStoryModal.module.scss';
import {TrashBin} from '@gravity-ui/icons';

const FORMAT = 'DD.MM.YYYY';

interface AddAppealStoryModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  appealId: string;
}

export const AddAppealStoryModal = ({open, setOpen, appealId}: AddAppealStoryModalProps) => {
  const [previewImageData, setPreviewImageData] = useState<PreparedImageData | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isLoading,
    submitError,
    resetForm,
    submitForm,
    setFieldValue,
  } = useAddAppealStoryForm({appealId, setOpen});

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'История сбора',
    title: 'Ошибка при добавлении истории сбора',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  // Очистка формы при открытии модального окна
  useEffect(() => {
    if (open) {
      resetForm();
      setPreviewImageData(null);
      setPreviewUrl('');
    }
  }, [open, resetForm]);

  // Обновляем uploadId в форме когда изображение загружено
  useEffect(() => {
    if (previewImageData) {
      setFieldValue('previewImageUploadId', previewImageData.uploadId);
    }
  }, [previewImageData, setFieldValue]);

  const handleImagePrepared = (data: PreparedImageData, preview: string) => {
    setPreviewImageData(data);
    setPreviewUrl(preview);
  };

  const handleDateChange = (date: DateTime | null) => {
    setFieldValue('date', date);
  };

  const handleRemoveImage = () => {
    setPreviewImageData(null);
    setPreviewUrl('');
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Добавить историю сбора'}
        textButtonApply={'Добавить'}
        textButtonCancel={'Отменить'}>
        <FormInput
          label="Заголовок истории"
          placeholder="Заголовок истории"
          name="title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired
          error={touched.title && errors.title}
        />
        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Дата <span style={{color: 'red'}}>*</span>
          </Text>
          <DatePicker
            onUpdate={handleDateChange}
            size="xl"
            className={styles.datePicker}
            value={values.date}
            placeholder="ДД.ММ.ГГГГ"
            format={FORMAT}
            hasClear
          />
          {touched.date && errors.date && (
            <Text variant="caption-2" color="danger" style={{marginTop: '4px'}}>
              {errors.date}
            </Text>
          )}
        </div>

        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Превью изображение <span style={{color: 'red'}}>*</span>
          </Text>
          <div>
            {!previewUrl && (
              <ImageUploadButton
                imageType={undefined}
                isCreating={true}
                aspectRatio={1 / 1}
                onPrepareImageData={handleImagePrepared}
                className={styles.imageUploadButton}
              />
            )}

            {previewUrl && (
              <div className={styles.previewImageContainer}>
                <img src={previewUrl} alt="Превью" className={styles.previewImage} />
                <Button view="raised" size="xs" onClick={handleRemoveImage} className={styles.removeImageBtn}>
                  <Icon data={TrashBin} size={14} />
                </Button>
              </div>
            )}
          </div>
          {touched.previewImageUploadId && errors.previewImageUploadId && (
            <Text variant="caption-2" color="danger" style={{marginTop: '4px'}}>
              {errors.previewImageUploadId}
            </Text>
          )}
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
